import { useState, useEffect, useRef, useCallback } from 'react';

import type { Agent, Task } from './api';
import { api } from './api';

type Tab = 'dashboard' | 'agents' | 'tasks' | 'execute' | 'memory';

const AGENT_TYPE_META = [
  {
    id: 'code',
    name: 'Orion',
    icon: '✦',
    color: '#22c55e',
    desc: 'Code generation, refactoring, debugging',
  },
  {
    id: 'research',
    name: 'Vega',
    icon: '◆',
    color: '#f59e0b',
    desc: 'Deep reasoning, research, analysis',
  },
  {
    id: 'debug',
    name: 'Draco',
    icon: '⬢',
    color: '#ef4444',
    desc: 'Security scanning, vulnerability detection',
  },
  {
    id: 'deploy',
    name: 'Atlas',
    icon: '⚙',
    color: '#8b5cf6',
    desc: 'Meta-orchestration, tool use, workflow',
  },
  { id: 'web', name: 'Web', icon: '◈', color: '#3b82f6', desc: 'Browser automation, web scraping' },
];

const s = {
  flexCenter: { display: 'flex' as const, alignItems: 'center' as const },
  card: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
  },
};

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div style={{ ...s.card, padding: '20px 24px', flex: 1, minWidth: 160 }}>
      <div
        style={{
          fontSize: 11,
          color: 'rgba(255,255,255,0.35)',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          fontFamily: "'Rajdhani', sans-serif",
          color: color || '#D6A94E',
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    running: '#22c55e',
    idle: '#a3a3a3',
    completed: '#22c55e',
    failed: '#ef4444',
    cancelled: '#f59e0b',
    queued: '#3b82f6',
  };
  return (
    <span
      style={{
        fontSize: 11,
        padding: '3px 10px',
        borderRadius: 6,
        background: `${colors[status] || '#666'}15`,
        color: colors[status] || '#666',
        fontWeight: 600,
        letterSpacing: '0.3px',
      }}
    >
      {status.toUpperCase()}
    </span>
  );
}

function TaskStream({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const [logs, setLogs] = useState<string[]>([]);
  const [status, setStatus] = useState('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ws = new WebSocket(api.wsTaskUrl(taskId));
    wsRef.current = ws;
    ws.onopen = () => setStatus('connected');
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === 'status') setStatus(msg.status);
      if (msg.type === 'log') setLogs((prev) => [...prev, msg.message]);
      if (msg.type === 'result') setLogs((prev) => [...prev, `── Result ──`, msg.result]);
    };
    ws.onerror = () => setStatus('error');
    ws.onclose = () => setStatus('disconnected');
    return () => ws.close();
  }, [taskId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: 500,
        height: 360,
        background: '#0a0a0a',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px 0 0 0',
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          ...s.flexCenter,
          justifyContent: 'space-between',
          padding: '10px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        <div style={{ ...s.flexCenter, gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Task {taskId.slice(-8)}</span>
          <StatusBadge status={status} />
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.4)',
            cursor: 'pointer',
            fontSize: 16,
          }}
        >
          ✕
        </button>
      </div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 16px',
          fontFamily: "'Fira Code', monospace",
          fontSize: 12,
          lineHeight: 1.6,
        }}
      >
        {logs.map((l, i) => (
          <div
            key={i}
            style={{
              color: l.startsWith('──') ? '#D6A94E' : 'rgba(255,255,255,0.7)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {l}
          </div>
        ))}
        {logs.length === 0 && (
          <div style={{ color: 'rgba(255,255,255,0.2)' }}>Waiting for output...</div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

export default function App() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedType, setSelectedType] = useState('code');
  const [agentName, setAgentName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [streamTaskId, setStreamTaskId] = useState<string | null>(null);
  const [promptInput, setPromptInput] = useState('');
  const [executeResult, setExecuteResult] = useState('');
  const [memories, setMemories] = useState<{ key: string; value: string }[]>([]);
  const [memKey, setMemKey] = useState('');
  const [memVal, setMemVal] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [a, t] = await Promise.all([api.getAgents(), api.getTasks()]);
      setAgents(a);
      setTasks(t);
    } catch (e: any) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === 'memory')
      api.memory
        .get()
        .then((r) => setMemories([...r.short_term, ...r.long_term]))
        .catch(() => {});
  }, [tab]);

  const handleCreateAgent = async () => {
    try {
      await api.createAgent({
        name: agentName || `${selectedType}-agent`,
        type: selectedType,
        system_prompt: systemPrompt || undefined,
      });
      setShowCreate(false);
      setAgentName('');
      setSystemPrompt('');
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleExecute = async () => {
    if (!promptInput) return;
    setExecuteResult('Running...');
    try {
      const r = await api.execute({ prompt: promptInput });
      setExecuteResult(r.result);
    } catch (e: any) {
      setExecuteResult(`Error: ${e.message}`);
    }
  };

  const handleCreateTask = async (agentId: string) => {
    const prompt = promptInput || 'run diagnostics';
    try {
      const t = await api.createTask({ agent_id: agentId, prompt });
      setStreamTaskId(t.id);
      setPromptInput('');
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDeleteAgent = async (id: string) => {
    try {
      await api.deleteAgent(id);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleCancelTask = async (id: string) => {
    try {
      await api.cancelTask(id);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleStoreMem = async () => {
    if (!memKey || !memVal) return;
    try {
      await api.memory.store(memKey, memVal);
      setMemKey('');
      setMemVal('');
      const r = await api.memory.get();
      setMemories([...r.short_term, ...r.long_term]);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const navItems: { id: Tab; label: string; icon: string }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: '⊞' },
    { id: 'agents', label: 'Agents', icon: '✦' },
    { id: 'tasks', label: 'Tasks', icon: '▷' },
    { id: 'execute', label: 'Execute', icon: '▶' },
    { id: 'memory', label: 'Memory', icon: '◫' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        background: '#050505',
        color: '#f5f5f5',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <aside
        style={{
          width: 200,
          background: '#0a0a0a',
          borderRight: '1px solid rgba(255,255,255,0.04)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >
        <div
          style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
        >
          <div style={{ ...s.flexCenter, gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg,#8b5cf6,#D6A94E)',
                ...s.flexCenter,
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 700,
                color: '#050505',
              }}
            >
              A
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  lineHeight: 1.2,
                }}
              >
                Atlas
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                }}
              >
                Control Plane
              </div>
            </div>
          </div>
        </div>
        <nav style={{ flex: 1, padding: '12px 10px' }}>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                border: 'none',
                fontSize: 13,
                fontWeight: 500,
                background: tab === item.id ? 'rgba(139,92,246,0.08)' : 'none',
                color: tab === item.id ? '#8b5cf6' : 'rgba(255,255,255,0.5)',
                fontFamily: "'Inter', sans-serif",
                transition: 'all 0.15s',
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>
        <div
          style={{
            padding: '16px 20px',
            borderTop: '1px solid rgba(255,255,255,0.04)',
            fontSize: 11,
            color: 'rgba(255,255,255,0.2)',
            lineHeight: 1.6,
          }}
        >
          <div>v1.1.0</div>
          <div>{agents.filter((a) => a.status === 'running').length} active</div>
        </div>
      </aside>

      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            ...s.flexCenter,
            justifyContent: 'space-between',
            padding: '14px 28px',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            background: 'rgba(5,5,5,0.8)',
            backdropFilter: 'blur(12px)',
            flexShrink: 0,
          }}
        >
          <h1
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            {navItems.find((n) => n.id === tab)?.label}
          </h1>
          <div style={{ ...s.flexCenter, gap: 10 }}>
            <button
              onClick={() => setShowCreate(true)}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg,#8b5cf6,#D6A94E)',
                border: 'none',
                borderRadius: 8,
                color: '#050505',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              + New Agent
            </button>
          </div>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: '28px' }}>
          {error && (
            <div
              style={{
                padding: '12px 16px',
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 8,
                color: '#ef4444',
                fontSize: 13,
                marginBottom: 20,
              }}
            >
              {error}
            </div>
          )}

          {tab === 'dashboard' && (
            <div style={{ maxWidth: 960 }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
                <StatCard
                  label="Agents"
                  value={String(agents.length)}
                  sub={
                    loading
                      ? 'loading...'
                      : `${agents.filter((a) => a.status === 'running').length} running`
                  }
                  color="#22c55e"
                />
                <StatCard
                  label="Tasks"
                  value={String(tasks.length)}
                  sub={
                    loading
                      ? 'loading...'
                      : `${tasks.filter((t) => t.status === 'running').length} running`
                  }
                  color="#8b5cf6"
                />
                <StatCard
                  label="Agent Types"
                  value={String(AGENT_TYPE_META.length)}
                  sub="Orion, Vega, Draco, Atlas, Web"
                  color="#D6A94E"
                />
                <StatCard
                  label="Connection"
                  value={error ? 'OFFLINE' : 'LIVE'}
                  color={error ? '#ef4444' : '#22c55e'}
                />
              </div>
              <div style={{ ...s.card, padding: 24 }}>
                <h2
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    marginBottom: 16,
                    color: '#8b5cf6',
                  }}
                >
                  Agent Types
                </h2>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                    gap: 12,
                  }}
                >
                  {AGENT_TYPE_META.map((at) => (
                    <div
                      key={at.id}
                      style={{
                        padding: 16,
                        borderRadius: 10,
                        border: `1px solid ${at.color}20`,
                        background: `${at.color}08`,
                      }}
                    >
                      <div style={{ fontSize: 24, marginBottom: 8 }}>{at.icon}</div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{at.name}</div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'rgba(255,255,255,0.35)',
                          marginTop: 4,
                          lineHeight: 1.4,
                        }}
                      >
                        {at.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'agents' && (
            <div style={{ maxWidth: 960 }}>
              {loading ? (
                <div style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: 40 }}>
                  Loading agents...
                </div>
              ) : agents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>✦</div>
                  <p>No agents deployed yet. Click "New Agent" to create one.</p>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 16,
                  }}
                >
                  {agents.map((agent) => {
                    const meta = AGENT_TYPE_META.find((t) => t.id === agent.agent_type);
                    const color = meta?.color || '#a3a3a3';
                    return (
                      <div
                        key={agent.id}
                        style={{
                          ...s.card,
                          padding: 20,
                          borderLeft: `3px solid ${agent.status === 'running' ? '#22c55e' : color}`,
                          position: 'relative',
                        }}
                      >
                        <button
                          onClick={() => handleDeleteAgent(agent.id)}
                          style={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            background: 'none',
                            border: 'none',
                            color: 'rgba(255,255,255,0.2)',
                            cursor: 'pointer',
                            fontSize: 14,
                            padding: 4,
                          }}
                          title="Delete agent"
                        >
                          ✕
                        </button>
                        <div style={{ ...s.flexCenter, gap: 10, marginBottom: 12 }}>
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              background: `${color}15`,
                              ...s.flexCenter,
                              justifyContent: 'center',
                              fontSize: 18,
                            }}
                          >
                            {meta?.icon || '?'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{agent.name}</div>
                            <div style={{ fontSize: 11, color, fontWeight: 600 }}>
                              {meta?.name || agent.agent_type}
                            </div>
                          </div>
                          <div style={{ marginLeft: 'auto' }}>
                            <StatusBadge status={agent.status} />
                          </div>
                        </div>
                        <div
                          style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}
                        >
                          <div>Model: {agent.model}</div>
                          <div>Tasks: {agent.tasks_completed}</div>
                          <div>Created: {agent.created_at.slice(0, 10)}</div>
                          <div>Tools: {agent.tools.join(', ')}</div>
                        </div>
                        <button
                          onClick={() => handleCreateTask(agent.id)}
                          style={{
                            marginTop: 12,
                            width: '100%',
                            padding: '8px',
                            background: 'rgba(139,92,246,0.1)',
                            border: '1px solid rgba(139,92,246,0.2)',
                            borderRadius: 8,
                            color: '#8b5cf6',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif",
                          }}
                        >
                          Run Task
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === 'tasks' && (
            <div style={{ maxWidth: 960 }}>
              {loading ? (
                <div style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: 40 }}>
                  Loading tasks...
                </div>
              ) : tasks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)' }}>
                  <p>No tasks yet. Create an agent and run a task.</p>
                </div>
              ) : (
                <div style={{ ...s.card, overflow: 'hidden' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                      gap: 12,
                      padding: '12px 20px',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.3)',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    <span>Prompt</span>
                    <span>Agent</span>
                    <span>Steps</span>
                    <span>Status</span>
                    <span>Action</span>
                  </div>
                  {tasks
                    .slice()
                    .reverse()
                    .map((task) => (
                      <div
                        key={task.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                          gap: 12,
                          padding: '14px 20px',
                          borderBottom: '1px solid rgba(255,255,255,0.03)',
                          fontSize: 13,
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 300,
                            }}
                          >
                            {task.prompt}
                          </div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                            {task.id.slice(-8)}
                          </div>
                        </div>
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                          {task.agent_id === 'direct' ? 'direct' : task.agent_id.slice(-8)}
                        </span>
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                          {task.steps}
                        </span>
                        <StatusBadge status={task.status} />
                        <div>
                          {task.status === 'running' && (
                            <button
                              onClick={() => handleCancelTask(task.id)}
                              style={{
                                background: 'rgba(239,68,68,0.1)',
                                border: '1px solid rgba(239,68,68,0.2)',
                                borderRadius: 6,
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: 11,
                                padding: '4px 10px',
                                fontFamily: "'Inter', sans-serif",
                              }}
                            >
                              Cancel
                            </button>
                          )}
                          {task.status === 'completed' && (
                            <button
                              onClick={() => setStreamTaskId(task.id)}
                              style={{
                                background: 'rgba(34,197,94,0.1)',
                                border: '1px solid rgba(34,197,94,0.2)',
                                borderRadius: 6,
                                color: '#22c55e',
                                cursor: 'pointer',
                                fontSize: 11,
                                padding: '4px 10px',
                                fontFamily: "'Inter', sans-serif",
                              }}
                            >
                              Result
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {tab === 'execute' && (
            <div style={{ maxWidth: 640 }}>
              <div style={{ ...s.card, padding: 24 }}>
                <h3
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    marginBottom: 12,
                    color: '#8b5cf6',
                  }}
                >
                  Execute Prompt
                </h3>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>
                  Run a prompt directly against any model
                </p>
                <textarea
                  value={promptInput}
                  onChange={(e) => setPromptInput(e.target.value)}
                  placeholder="Enter your prompt..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8,
                    color: '#f5f5f5',
                    fontSize: 13,
                    fontFamily: "'Inter', sans-serif",
                    outline: 'none',
                    resize: 'vertical',
                    marginBottom: 12,
                  }}
                />
                <button
                  onClick={handleExecute}
                  disabled={!promptInput}
                  style={{
                    padding: '10px 24px',
                    background: 'linear-gradient(135deg,#8b5cf6,#D6A94E)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#050505',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: promptInput ? 'pointer' : 'not-allowed',
                    opacity: promptInput ? 1 : 0.5,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Execute
                </button>
              </div>
              {executeResult && (
                <div style={{ ...s.card, padding: 24, marginTop: 16 }}>
                  <h3
                    style={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 8,
                      color: '#22c55e',
                    }}
                  >
                    Result
                  </h3>
                  <pre
                    style={{
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.7)',
                      whiteSpace: 'pre-wrap',
                      fontFamily: "'Fira Code', monospace",
                      lineHeight: 1.5,
                      maxHeight: 400,
                      overflow: 'auto',
                    }}
                  >
                    {executeResult}
                  </pre>
                </div>
              )}
            </div>
          )}

          {tab === 'memory' && (
            <div style={{ maxWidth: 640 }}>
              <div style={{ ...s.card, padding: 24, marginBottom: 16 }}>
                <h3
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    marginBottom: 12,
                    color: '#8b5cf6',
                  }}
                >
                  Store Memory
                </h3>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    value={memKey}
                    onChange={(e) => setMemKey(e.target.value)}
                    placeholder="Key"
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 8,
                      color: '#f5f5f5',
                      fontSize: 13,
                      fontFamily: "'Inter', sans-serif",
                      outline: 'none',
                    }}
                  />
                  <input
                    value={memVal}
                    onChange={(e) => setMemVal(e.target.value)}
                    placeholder="Value"
                    style={{
                      flex: 2,
                      padding: '10px 14px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 8,
                      color: '#f5f5f5',
                      fontSize: 13,
                      fontFamily: "'Inter', sans-serif",
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleStoreMem}
                    style={{
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg,#8b5cf6,#D6A94E)',
                      border: 'none',
                      borderRadius: 8,
                      color: '#050505',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    Store
                  </button>
                </div>
              </div>
              <div style={{ ...s.card, padding: 24 }}>
                <h3
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    marginBottom: 12,
                    color: '#D6A94E',
                  }}
                >
                  Stored Memories ({memories.length})
                </h3>
                {memories.length === 0 ? (
                  <div
                    style={{
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.2)',
                      textAlign: 'center',
                      padding: 20,
                    }}
                  >
                    No memories stored
                  </div>
                ) : (
                  memories.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '10px 0',
                        borderBottom: '1px solid rgba(255,255,255,0.03)',
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{m.key}:</span>{' '}
                      <span style={{ color: 'rgba(255,255,255,0.6)' }}>{m.value}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {showCreate && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 100,
            ...s.flexCenter,
            justifyContent: 'center',
          }}
          onClick={() => setShowCreate(false)}
        >
          <div
            style={{
              background: '#0a0a0a',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16,
              width: 480,
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 20, fontWeight: 700 }}>
                Create Agent
              </h2>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                Deploys to atlas-control-plane API (port 8093)
              </p>
            </div>
            <div style={{ padding: '24px 28px' }}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  Agent Type
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                  {AGENT_TYPE_META.map((at) => (
                    <button
                      key={at.id}
                      onClick={() => setSelectedType(at.id)}
                      style={{
                        padding: 12,
                        borderRadius: 8,
                        cursor: 'pointer',
                        background: selectedType === at.id ? `${at.color}15` : `${at.color}08`,
                        border:
                          selectedType === at.id
                            ? `2px solid ${at.color}`
                            : `1px solid ${at.color}20`,
                        color: '#f5f5f5',
                        fontFamily: "'Inter', sans-serif",
                        textAlign: 'center',
                        transition: 'all 0.1s',
                      }}
                    >
                      <div style={{ fontSize: 20, marginBottom: 4 }}>{at.icon}</div>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>{at.name}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  Agent Name
                </label>
                <input
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder={`${selectedType}-agent`}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8,
                    color: '#f5f5f5',
                    fontSize: 13,
                    fontFamily: "'Inter', sans-serif",
                    outline: 'none',
                  }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  System Prompt
                </label>
                <textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={3}
                  placeholder="Custom instructions for this agent..."
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8,
                    color: '#f5f5f5',
                    fontSize: 13,
                    fontFamily: "'Inter', sans-serif",
                    outline: 'none',
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>
            <div
              style={{
                padding: '16px 28px',
                borderTop: '1px solid rgba(255,255,255,0.04)',
                ...s.flexCenter,
                justifyContent: 'flex-end',
                gap: 10,
              }}
            >
              <button
                onClick={() => setShowCreate(false)}
                style={{
                  padding: '8px 20px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8,
                  color: '#f5f5f5',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAgent}
                style={{
                  padding: '8px 20px',
                  background: 'linear-gradient(135deg,#8b5cf6,#D6A94E)',
                  border: 'none',
                  borderRadius: 8,
                  color: '#050505',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Deploy Agent
              </button>
            </div>
          </div>
        </div>
      )}

      {streamTaskId && <TaskStream taskId={streamTaskId} onClose={() => setStreamTaskId(null)} />}
    </div>
  );
}
